import React, {useCallback, useRef} from 'react';
import {type NodeLocationInfo} from 'Contexts/NodeCallBackContext';
import {getEdgesAndOffsets} from 'utils/getEdgesAndOffsets';
import {useAnimationFrame} from 'Hooks/useAnimationFrame';
import {AstView, EnrichedAstNode} from 'rendition-core';
import {AbsoluteUnitsSelect} from './AbsoluteUnitsSelect';

interface Props {
    isTopHandle?: boolean;
    itemWidth: number;
    itemHeight: number;
    relativeParentHeight: number;
    xy: () => NodeLocationInfo['actual'];
    locationInfo: () => NodeLocationInfo;
    rootLocationInfo: () => NodeLocationInfo;
    node: EnrichedAstNode;
    view: AstView;
    setView: (view: AstView) => void;
}

const DEFAULT_WIDTH = 50;
const BORDER_WIDTH = 1;

const UNITS_Y_OFFSET = 35;

export const VerticalHandle: React.FC<Props> = ({
    xy,
    locationInfo,
    rootLocationInfo,
    itemWidth,
    isTopHandle,
    relativeParentHeight,
    node,
    view,
    setView,
    ...props
}) => {
    const ref = useRef<null | HTMLDivElement>(null);
    const unitRef = useRef<null | HTMLDivElement>(null);
    const height = useRef(0);

    const minWidth = itemWidth < DEFAULT_WIDTH && itemWidth > 20 ? itemWidth - 10 : 10;
    const width = itemWidth < DEFAULT_WIDTH ? minWidth : DEFAULT_WIDTH;

    const isBottom = node.inputLayoutAbstraction.verticalAnchor === 'bottom';
    const isTop = !isBottom;

    const updateOnFrame = useCallback(() => {
        if (ref.current && unitRef.current) {
            // callback
            const edgesAndOffsets = getEdgesAndOffsets(xy, locationInfo, rootLocationInfo);

            const topNegative = edgesAndOffsets.topEdgeOfSelectedNode < 0;
            const bottomNegative = edgesAndOffsets.spaceBetweenBottomEdge < 0;

            const styles = ref.current.style;
            const unitStyles = unitRef.current.style;

            styles.top = '';
            styles.bottom = '';
            unitStyles.top = '';
            unitStyles.bottom = '';

            styles.left = `${edgesAndOffsets.leftEdgeOfSelectedNode + edgesAndOffsets.offsetLeft + itemWidth / 2}px`;
            height.current = isTopHandle
                ? Math.abs(edgesAndOffsets.topEdgeOfSelectedNode) - (topNegative ? 0 : BORDER_WIDTH)
                : Math.abs(edgesAndOffsets.spaceBetweenBottomEdge) - (bottomNegative ? 0 : BORDER_WIDTH);

            styles.height = `${height.current}px`;

            if (isTopHandle) {
                styles.top = `${edgesAndOffsets.offsetTop}px`;
                styles.bottom = `${edgesAndOffsets.topEdgeOfSelectedNode + edgesAndOffsets.offsetTop - BORDER_WIDTH}px`;
            } else {
                styles.top = `${edgesAndOffsets.bottomEdgeOfSelectedNode + edgesAndOffsets.offsetTop + BORDER_WIDTH}px`;
            }

            if (topNegative && isTopHandle) {
                styles.top = `${edgesAndOffsets.topEdgeOfSelectedNode + edgesAndOffsets.offsetTop}px`;
                styles.bottom = edgesAndOffsets.offsetTop + 'px';
                styles.height = height.current + 'px';
            } else if (bottomNegative && !isTopHandle) {
                styles.top = edgesAndOffsets.offsetTop + relativeParentHeight + 'px';
                styles.height = Math.abs(edgesAndOffsets.spaceBetweenBottomEdge) + 'px';
            }

            const unitsTop =
                edgesAndOffsets.topEdgeOfSelectedNode < 0
                    ? edgesAndOffsets.topEdgeOfSelectedNode + edgesAndOffsets.offsetTop - UNITS_Y_OFFSET - BORDER_WIDTH
                    : edgesAndOffsets.offsetTop - UNITS_Y_OFFSET;

            const unitsBottom =
                edgesAndOffsets.spaceBetweenBottomEdge < 0
                    ? edgesAndOffsets.bottomEdgeOfSelectedNode + edgesAndOffsets.offsetTop + BORDER_WIDTH + 8
                    : edgesAndOffsets.bottomEdgeOfSelectedNode +
                      edgesAndOffsets.offsetTop +
                      edgesAndOffsets.spaceBetweenBottomEdge +
                      BORDER_WIDTH +
                      8;

            unitStyles.left = `${
                edgesAndOffsets.leftEdgeOfSelectedNode + edgesAndOffsets.offsetLeft + itemWidth / 2 - 25
            }px`;
            unitStyles.top = (isTop ? unitsTop : unitsBottom) + 'px';
        }
    }, [isTop, isTopHandle, itemWidth, locationInfo, relativeParentHeight, rootLocationInfo, xy]);

    useAnimationFrame(updateOnFrame);

    return (
        <>
            <div
                ref={ref}
                style={{
                    position: 'absolute',
                    transform: 'translateX(-50%)',
                    width,
                    zIndex: 11,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxWidth: width,
                }}
            >
                <svg width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
                    <line
                        x1="50%"
                        x2="50%"
                        y1="0"
                        y2="100%"
                        strokeLinecap="round"
                        strokeDasharray="0.5 4"
                        stroke="#9688EE"
                        strokeWidth={2}
                    />
                </svg>
                <svg
                    style={{[isTop ? 'bottom' : 'top']: -5, position: 'absolute'}}
                    width="10"
                    height="10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    {...props}
                >
                    <circle cy="5" cx="5" r={4.5} fill="#7B74FF" stroke="black" />
                </svg>
            </div>

            <AbsoluteUnitsSelect
                ref={unitRef}
                anchor={isTop ? 'top' : 'bottom'}
                node={node}
                view={view}
                setView={setView}
            />
        </>
    );
};
